import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Coins, AlertCircle, Music } from "lucide-react";

/**
 * Confirmation modal / bottom sheet displayed when declining a request with a tip.
 * Inspired by Android's bottom_sheet_reject_tip_track.xml.
 */
export default function RejectTipModal({
  isOpen,
  onClose,
  onConfirmDecline,
  tipAmount,
  track,
  isLoading = false,
}) {
  // Disable body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const numTip = typeof tipAmount === "number" ? tipAmount : parseFloat(tipAmount);
  const formattedTip = !isNaN(numTip)
    ? numTip % 1 === 0
      ? numTip.toFixed(0)
      : numTip.toFixed(2)
    : tipAmount || "0";

  const trackName = track?.name || "this song";
  const artistNames =
    Array.isArray(track?.artists)
      ? track.artists.map((a) => a.name).join(", ")
      : typeof track?.artists === "string"
        ? track.artists
        : "";
  const albumImage = track?.album?.images?.[0]?.url;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs p-0 sm:p-4">
        {/* Backdrop click dismiss */}
        <div className="fixed inset-0" onClick={onClose} />

        {/* Modal / Bottom Sheet */}
        <motion.div
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 300 }}
          className="relative z-10 w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 sm:p-7 flex flex-col items-center border border-gray-100"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile Handle Bar */}
          <div className="w-12 h-1.5 bg-gray-300 rounded-full mb-3 sm:hidden" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-800 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Glowing Gold Circle with Coins / Rupee Badge */}
          <div className="relative mt-1 mb-3">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-300 via-amber-400 to-yellow-500 shadow-lg shadow-amber-400/30 flex items-center justify-center border-2 border-amber-200">
              <Coins className="w-8 h-8 text-amber-950" />
            </div>
            {/* Rupee icon badge */}
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#6720A4] border-2 border-white flex items-center justify-center text-white text-xs font-black shadow-xs">
              ₹
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl sm:text-2xl font-extrabold text-[#1F1035] text-center tracking-tight leading-tight">
            Say no to a ₹{formattedTip} tip?
          </h3>

          {/* Subtitle */}
          <p className="text-sm font-semibold text-gray-500 text-center mt-1">
            Still want to decline?
          </p>

          {/* Song Card Info */}
          {track && (
            <div className="w-full mt-3.5 p-3 bg-gray-50 border border-gray-200/80 rounded-2xl flex items-center gap-3">
              {albumImage ? (
                <img
                  src={albumImage}
                  alt={trackName}
                  className="w-12 h-12 rounded-xl object-cover border border-gray-150 shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-purple-100 text-[#6720A4] flex items-center justify-center shrink-0">
                  <Music className="w-6 h-6" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {trackName}
                </p>
                {artistNames && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {artistNames}
                  </p>
                )}
              </div>
              <div className="shrink-0 px-2 py-1 bg-amber-100 text-amber-900 rounded-lg text-xs font-bold border border-amber-300">
                ₹{formattedTip} Tip
              </div>
            </div>
          )}

          {/* Note / Tip refund disclaimer */}
          <div className="w-full mt-3 flex items-center gap-2 p-2.5 bg-amber-50/80 border border-amber-200/80 rounded-xl text-amber-800 text-xs">
            <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
            <span>
              If you decline, the tip will be refunded to the guest and you won’t earn it.
            </span>
          </div>

          {/* Actions */}
          <div className="w-full mt-5 space-y-2">
            {/* Primary Action: Keep Request */}
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="w-full py-3 px-6 rounded-2xl bg-[#6720A4] hover:bg-[#551887] active:scale-[0.98] text-white font-bold text-sm sm:text-base shadow-md shadow-purple-200 transition-all flex items-center justify-center gap-2"
            >
              Keep Request
            </button>

            {/* Secondary Action: Decline Anyway */}
            <button
              type="button"
              onClick={onConfirmDecline}
              disabled={isLoading}
              className="w-full py-2.5 px-4 rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50/80 font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
            >
              {isLoading ? "Declining..." : "Decline Anyway"}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
